import { useEffect, useState } from "react";
import { api, formatApiError, downloadPdf } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, X, Download, FileText } from "lucide-react";
import { toast } from "sonner";

export default function RHCertificates() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("en_attente");
  const [action, setAction] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/certificates"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const confirm = async () => {
    setSaving(true);
    try {
      await api.patch(`/certificates/${action.cert.id}`, { status: action.status, commentaire_rh: comment });
      toast.success(action.status === "approuve" ? "Attestation validée" : "Demande rejetée");
      setAction(null); setComment("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const dl = async (id) => {
    try { await downloadPdf(`/certificates/${id}/pdf`, `attestation_${id.slice(0,8)}.pdf`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((i) => tab === "tous" ? true : i.status === tab);

  return (
    <div>
      <PageHeader title="Attestations de travail" subtitle="Validez les demandes d'attestation pour générer le PDF officiel." />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="en_attente" data-testid="cert-tab-attente">En attente ({items.filter(i=>i.status==="en_attente").length})</TabsTrigger>
          <TabsTrigger value="approuve">Approuvés</TabsTrigger>
          <TabsTrigger value="rejete">Rejetés</TabsTrigger>
          <TabsTrigger value="tous">Tous</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card className="border-slate-200 overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="h-10 w-10 text-slate-300 mx-auto" />
                <p className="mt-3 text-sm text-slate-500">Aucune demande</p>
              </div>
            ) : (
              <>
              <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employé</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Destinataire</TableHead>
                    <TableHead>Motif</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c) => (
                    <TableRow key={c.id} data-testid={`rh-cert-row-${c.id}`}>
                      <TableCell>
                        <div className="font-medium">{c.user_name}</div>
                        <div className="text-xs text-slate-500">{c.user_email}</div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border ${c.type_attestation === "salaire" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {c.type_attestation === "salaire" ? "Salaire" : "Travail"}
                        </span>
                      </TableCell>
                      <TableCell>{c.destinataire || "—"}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-slate-600">{c.motif}</TableCell>
                      <TableCell className="text-sm text-slate-500">{new Date(c.created_at).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-right">
                        {c.status === "en_attente" ? (
                          <div className="flex gap-2 justify-end">
                            <Button data-testid={`approve-cert-${c.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAction({ cert: c, status: "approuve" })}><Check className="h-3.5 w-3.5 mr-1" />Valider</Button>
                            <Button data-testid={`reject-cert-${c.id}`} size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setAction({ cert: c, status: "rejete" })}><X className="h-3.5 w-3.5 mr-1" />Rejeter</Button>
                          </div>
                        ) : c.status === "approuve" ? (
                          <Button size="sm" variant="outline" onClick={() => dl(c.id)}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
              <div className="md:hidden divide-y divide-slate-100">
                {filtered.map((c) => (
                  <div key={c.id} className="p-4" data-testid={`rh-cert-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{c.user_name}</div>
                        <div className="text-xs text-slate-500 truncate">{c.user_email}</div>
                      </div>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border ${c.type_attestation === "salaire" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                        {c.type_attestation === "salaire" ? "Salaire" : "Travail"}
                      </span>
                      <span className="truncate">Pour : {c.destinataire || "à qui de droit"}</span>
                    </div>
                    {c.motif && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{c.motif}</p>}
                    {c.status === "en_attente" ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Button data-testid={`approve-cert-m-${c.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAction({ cert: c, status: "approuve" })}><Check className="h-3.5 w-3.5 mr-1" />Valider</Button>
                        <Button data-testid={`reject-cert-m-${c.id}`} size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setAction({ cert: c, status: "rejete" })}><X className="h-3.5 w-3.5 mr-1" />Rejeter</Button>
                      </div>
                    ) : c.status === "approuve" ? (
                      <Button size="sm" variant="outline" className="w-full" onClick={() => dl(c.id)}><Download className="h-3.5 w-3.5 mr-1" />Télécharger le PDF</Button>
                    ) : null}
                  </div>
                ))}
              </div>
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!action} onOpenChange={(o) => { if (!o) { setAction(null); setComment(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{action?.status === "approuve" ? "Valider l'attestation" : "Rejeter la demande"}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600">Employé : <span className="font-medium text-slate-900">{action?.cert?.user_name}</span></div>
          <Textarea placeholder="Commentaire (facultatif)" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAction(null); setComment(""); }}>Annuler</Button>
            <Button onClick={confirm} disabled={saving} className={action?.status === "approuve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}>{saving ? "…" : "Confirmer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
